import { GroupOutlined, LogoutOutlined, NotificationOutlined, ProfileOutlined, RestOutlined, SettingOutlined, ShopOutlined, TeamOutlined, UserOutlined, BankOutlined, ControlOutlined } from "@ant-design/icons";
import ForkKnifeIcon from "components/icons/ForkKnifeIcon";
import { Grid, Menu, Tag } from "antd";
import { useNavigate } from "react-router-dom";
import { useAuth } from "contexts/authContext";
import { useOrg } from "contexts/orgContext";
import { ItemType, MenuItemType } from "antd/es/menu/interface";
import { doSignOut } from "firebase-utils/auth";
import { useMemo, useCallback } from "react";
import type { Org } from "models";
import { CategoryType } from "models";
import { capitalCase } from "change-case";
import { getEnvironmentBadges } from "utils/envUtil";
import { useTheme } from "hooks/useTheme";
import SbdWordmark from "components/brand/SbdWordmark";
import BarrelMark from "components/brand/BarrelMark";

interface AppSideMenuProps {
  collapsed?: boolean;
  onMenuClick?: () => void;
}

const AppSideMenu: React.FC<AppSideMenuProps> = ({ collapsed, onMenuClick }) => {

  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const navigate = useNavigate();
  const { roles } = useAuth();
  const { orgs, setCurrentOrg } = useOrg();
  const { isDark: dark } = useTheme();
  
  const handleNavigate = useCallback((path: string) => {
    navigate(path);
    onMenuClick?.(); // Close mobile menu after navigation
  }, [navigate, onMenuClick]);

  const handleOrgChange = useCallback((org: Org) => {
    setCurrentOrg(org);
    navigate('/'); // Navigate to Home view after setting org
    onMenuClick?.(); // Close mobile menu after org change
  }, [setCurrentOrg, navigate, onMenuClick]);
  
  const categoryTypeMenuItems: ItemType<MenuItemType>[] = useMemo(() => {
    return Object.values(CategoryType).map((type) => {
      const path = type === CategoryType.Beverage ? '/beverages' :
                   `/special-items?categoryType=${type}`;

      const icon = type === CategoryType.Beverage ? <RestOutlined /> :
                   type === CategoryType.Merchandise ? <ShopOutlined /> :
                   type === CategoryType.Food ? <ForkKnifeIcon /> :
                   type === CategoryType.Promotional ? <NotificationOutlined /> :
                   <GroupOutlined />;

      return {
        key: type,
        icon: icon,
        label: capitalCase(type),
        onClick: () => handleNavigate(path)
      };
    });
  }, [handleNavigate]);

  const orgMenuItems: ItemType<MenuItemType>[] = useMemo(() => {
    return orgs.map((org) => ({
      key: `org-${org.id}`,
      icon: <TeamOutlined />,
      label: org.displayName,
      onClick: () => handleOrgChange(org)
    }));
  }, [orgs, handleOrgChange]);

  const menuItems: ItemType<MenuItemType>[] = [
    { key: 'menus', icon: <ProfileOutlined />, label: "Menus", onClick: () => handleNavigate('/menus') }, 
    ...categoryTypeMenuItems,
    { key: 'categories', icon: <GroupOutlined />, label: "Categories", onClick: () => handleNavigate('/categories') }
  ];

  // Add Organizations menu if user has access to multiple orgs
  if (orgs.length > 1) {
    menuItems.push(
      { key: 'organizations', icon: <BankOutlined />, label: "Organizations", children: orgMenuItems }
    );
  }

  if (roles?.some(item => ['ADMIN', 'GLOBAL_ADMIN'].includes(item))) {
    const adminChildren = [];
    
    // Add Organization Settings for ADMIN users
    if (roles.includes('ADMIN')) {
      adminChildren.push({
        key: 'settings',
        icon: <ControlOutlined />,
        label: "Settings",
        onClick: () => handleNavigate('/admin/organization')
      });
    }
    
    // Add Users for ADMIN users
    if (roles.includes('ADMIN')) {
      adminChildren.push({
        key: 'users',
        icon: <UserOutlined />,
        label: "Users",
        onClick: () => handleNavigate('/admin/users')
      });
    }

    // Add Organizations for GLOBAL_ADMIN users
    if (roles.includes('GLOBAL_ADMIN')) {
      adminChildren.push({
        key: 'orgs',
        icon: <BankOutlined />,
        label: "Organizations",
        onClick: () => handleNavigate('/admin/orgs')
      });
    }

    menuItems.push({
      key: 'admin',
      icon: <SettingOutlined />,
      label: "Admin",
      children: adminChildren
    });
  }

  menuItems.push(
    {
      key: 'signOut',
      icon: <LogoutOutlined />,
      label: "Logout",
      onClick: () => {
        doSignOut().then(() => {
          handleNavigate('/login');
        });
      }
    }
  )

  const envBadges = getEnvironmentBadges();

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: collapsed ? '14px 0' : '14px 16px',
        borderBottom: '0.5px solid var(--sbd-border)',
      }}>
        {collapsed ? (
          <BarrelMark variant={dark ? 'dark' : 'light'} width={28} height={36} aria-label="Small Batch Digital" />
        ) : (
          <SbdWordmark variant={dark ? 'dark' : 'light'} />
        )}
      </div>
      <Menu
        mode="inline"
        defaultSelectedKeys={['1']}
        defaultOpenKeys={['sub1']}
        style={{ flex: 1, borderRight: 0 }}
        items={menuItems}
      />
      {envBadges.length > 0 && roles.includes('GLOBAL_ADMIN') && !collapsed && screens.md && (
        <div style={{
          padding: '12px 16px',
          margin: '12px 0px',
          borderTop: '0.5px solid var(--sbd-border)',
          display: 'flex',
          gap: 6,
          flexWrap: 'wrap',
        }}>
          {envBadges.map((badge) => (
            <Tag key={badge.label} color={badge.color} style={{ margin: 0 }}>
              {badge.label}
            </Tag>
          ))}
        </div>
      )}
    </div>
  );
}

export default AppSideMenu;